import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { Card, Row, Col, Form, OverlayTrigger, Tooltip, InputGroup } from 'react-bootstrap';
import DatePicker from "react-datepicker";
import axios from 'axios';
import debounce from 'lodash/debounce';
import Select from 'react-select';
import config from '../../config';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { v4 as uuidv4 } from 'uuid';

const options = [
    { value: '30', label: '30 Days' },
    { value: '60', label: '60 Days' },
    { value: '90', label: '90 Days' },
    { value: '120', label: '120 Days' },
    { value: '0', label: 'Other' },
];

const runoutBeginOptions = [
    { value: 'GracePeriodEnd', label: 'At Grace Period End' },
    { value: 'PlanYearEnd', label: 'At Plan Year End' },
];

const employerContributionOptions = [
    { value: 'A flat contribution for all up to a maximum of $500', label: 'A flat contribution for all up to a maximum of $500' },
    { value: 'A dollar for dollar match', label: 'A dollar for dollar match' },
    { value: 'Combination of flat amount and dollar for dollar match', label: 'Combination of flat amount and dollar for dollar match' },
    { value: 'Wellness based (all participants must be able to earn the same wellness funding)', label: 'Wellness based (all participants must be able to earn the same wellness funding)' },
    
];

const employerContributionDcaOptions = [
    { value: 'Up front', label: 'Up front' },
    { value: 'Per pay period', label: 'Per pay period' },
];

const EmployerContributionMethods = [
    { value: 'Contribution Manager', label: 'Contribution Manager' },
    { value: 'Deposit File', label: 'Deposit File' },
];




const MinMaxInput = ({
    minValue,
    maxValue,
    minPlaceholder,
    maxPlaceholder,
    minValidation,
    maxValidation,
    updateMin,
    updateMax,
    tooltip,
    disabled
}) => {
    const [minError, setMinError] = useState('');
    const [maxError, setMaxError] = useState('');
    const [rangeError, setRangeError] = useState('');
    const [minFocused, setMinFocused] = useState(false); // Track if the min input is focused
    const [maxFocused, setMaxFocused] = useState(false); // Track if the max input is focused

    const validateMin = (value) => {
        if (value === '') {
            setMinError('');
            return;
        }
        const numericValue = parseFloat(value);
        if (isNaN(numericValue) || numericValue < minValidation.min || numericValue > minValidation.max) {
            setMinError(`Value must be between ${minValidation.min} and ${minValidation.max}`);
        } else {
            setMinError('');
        }
    };

    const validateMax = (value) => {
        if (value === '') {
            setMaxError('');
            return;
        }
        const numericValue = parseFloat(value);
        if (isNaN(numericValue) || numericValue < maxValidation.min || numericValue > maxValidation.max) {
            setMaxError(`Value must be between ${maxValidation.min} and ${maxValidation.max}`);
        } else {
            setMaxError('');
        }
    };

    const validateRange = (minVal, maxVal) => {
        const min = parseFloat(minVal);
        const max = parseFloat(maxVal);
        if (!isNaN(min) && !isNaN(max) && min > max) {
            setRangeError('Minimum value cannot be greater than maximum value');
        } else {
            setRangeError('');
        }
    };

    const formatCurrency = (value) => {
        const numericValue = parseFloat(value);
        return isNaN(numericValue) ? '' : `$${numericValue.toFixed(2)}`;
    };

    const handleMinChange = (value) => {
        const sanitizedValue = value.replace(/[^0-9.]/g, ''); // Allow only numbers and decimal
        updateMin(sanitizedValue); // Update internal state with raw number
        validateMin(sanitizedValue);
        validateRange(sanitizedValue, maxValue);
    };

    const handleMaxChange = (value) => {
        const sanitizedValue = value.replace(/[^0-9.]/g, ''); // Allow only numbers and decimal
        updateMax(sanitizedValue); // Update internal state with raw number
        validateMax(sanitizedValue);
        validateRange(minValue, sanitizedValue);
    };

    return (
        <InputGroup className="mb-3">
            <Form.Control
                type="text"
                value={minFocused ? minValue : formatCurrency(minValue)} // Format only when not focused
                placeholder={minPlaceholder}
                onChange={(e) => handleMinChange(e.target.value)} // Handle change with sanitization
                onFocus={() => setMinFocused(true)} // Set focused state
                onBlur={() => {
                    setMinFocused(false);
                    validateMin(minValue); // Validate on blur
                }} 
                title="Annual Election Min Amount"
                isInvalid={!!minError || !!rangeError}
                disabled={disabled}
            />
            <Form.Control
                type="text"
                value={maxFocused ? maxValue : formatCurrency(maxValue)} // Format only when not focused
                placeholder={maxPlaceholder}
                onChange={(e) => handleMaxChange(e.target.value)} // Handle change with sanitization
                onFocus={() => setMaxFocused(true)} // Set focused state
                onBlur={() => {
                    setMaxFocused(false);
                    validateMax(maxValue); // Validate on blur
                }}
                title="Annual Election Max Amount"
                isInvalid={!!maxError || !!rangeError}
                disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
                {minError || maxError || rangeError}
            </Form.Control.Feedback>
        </InputGroup>
    );
};





const FSA = forwardRef(({ caseId, renewalCase, isDisableForUsers, onError = () => { }, informationProvider }, ref) => {

    const [plans, setPlans] = useState({
        generalInfo: {},
        FSA: {},
        DCA: {},
        LPFSA: {},
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [OriginalLpfsa, setOriginalLpfsa] = useState({});

    useEffect(() => {
        const fetchFsa = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/EBFSAPlans/ByCaseId/${caseId}`);
                setPlans((prevPlans) => ({ ...prevPlans, FSA: response.data || {} }));
            } catch (error) {
                setError('');
            }
        };

        const fetchDca = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/EBDCAPlans/ByCaseId/${caseId}`);
                setPlans((prevPlans) => ({ ...prevPlans, DCA: response.data || {} }));
            } catch (error) {
                setError('');
            }
        };

        const fetchLpfsa = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/EBLPFSAPlans/ByCaseId/${caseId}`);
                setPlans((prevPlans) => ({ ...prevPlans, LPFSA: response.data || {} }));
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    setPlans((prevPlans) => ({ ...prevPlans, LPFSA: {} }));  // Pass empty data on 404
                } else {
                    setError('');
                }
            }
        };

        const fetchOriginalLpfsa = async () => {
            try {
                const response = await axios.get(
                    `${config.API_URL}/EBLPFSAPlans/OriginalData/ByCaseId/${caseId}`
                );
        
                setOriginalLpfsa(response.data || {});
            } catch (error) {
                if (error.response?.status === 404) {
                    setOriginalLpfsa({});
                } else {
                    console.error(error);
                    setError('Failed to fetch original LPFSA data.');
                }
            }
        };
        
        const fetchGeneralInfo = async () => {
            try {
                const response = await axios.get(`${config.API_URL}/EBGeneralInformations/ByCaseId/${caseId}`);
                setPlans((prevPlans) => ({ ...prevPlans, generalInfo: response.data || {} }));
            } catch (error) {
                setError('Error fetching plans.');
            } finally {
                setLoading(false);
            }
        };


        const fetchAllPlans = async () => {
            setLoading(true);
            await Promise.all([fetchGeneralInfo(), fetchFsa(), fetchDca(), fetchLpfsa(), fetchOriginalLpfsa()]);
            setLoading(false);
        };

        fetchAllPlans();
    }, [caseId]);

    const debouncedUpdate = useCallback(
        debounce(async (planType, updatedFields, fieldPlanId) => {
            try {
                const planId =
                    fieldPlanId ||
                    updatedFields?.planId ||
                    plans[planType]?.planId || plans[planType]?.id;
    
                
                if (!planId) {
                    console.warn(`Skipping PATCH for ${planType} - no planId`);
                    return;
                }
    
                const patchDocument = Object.keys(updatedFields).map((key) => ({
                    op: "replace",
                    path: `/${key}`,
                    value: updatedFields[key],
                }));
    
                if (planType === "generalInfo") {
                    await axios.patch(
                        `${config.API_URL}/EBGeneralInformations/${planId}`,
                        patchDocument
                    );
                } else {
                    await axios.patch(
                        `${config.API_URL}/EB${planType}Plans/${planId}`,
                        patchDocument
                    );
                }
    
            } catch (error) {
                console.error(`PATCH error (${planType}):`, error);
                onError(error);
            }
        }, 500),
        [plans]
    );


    const validateField = (name, value) => {
        let errors = { ...validationErrors };

        switch (name) {
            case 'fsaPlanStartDate':
                if (plans.FSA?.isFSA) {
                    if (!value) {
                        errors[name] = 'Plan start date is required';
                    } else {
                        delete errors[name];
                    }
                }
                break;

            case 'fsaPlanEndDate':
                if (plans.FSA?.isFSA) {
                    if (!value) {
                        errors[name] = 'Plan end date is required';
                    } else if (plans.FSA?.fsaPlanStartDate && new Date(value) < new Date(plans.FSA?.fsaPlanStartDate)) {
                        errors[name] = 'Plan end date must be after the start date';
                    } else {
                        delete errors[name];
                    }
                }
                break;

            case 'dcaPlanStartDate':
                if (plans.DCA?.isDCA) {
                    if (!value) {
                        errors[name] = 'Plan start date is required';
                    } else {
                        delete errors[name];
                    }
                }
                break;

            case 'dcaPlanEndDate':
                if (plans.DCA?.isDCA) {
                    if (!value) {
                        errors[name] = 'Plan end date is required';
                    } else if (plans.DCA?.dcaPlanStartDate && new Date(value) < new Date(plans.DCA?.dcaPlanStartDate)) {
                        errors[name] = 'Plan end date must be after the start date';
                    } else {
                        delete errors[name];
                    }
                }
                break;

            case 'lpfsaPlanStartDate':
                if (((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA)) {
                    if (!value) {
                        errors[name] = 'Plan start date is required';
                    } else {
                        delete errors[name];
                    }
                }
                break;

            case 'lpfsaPlanEndDate':
                if (((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA)) {
                    if (!value) {
                        errors[name] = 'Plan end date is required';
                    } else if (plans.LPFSA?.lpfsaPlanStartDate && new Date(value) < new Date(plans.LPFSA?.lpfsaPlanStartDate)) {
                        errors[name] = 'Plan end date must be after the start date';
                    } else {
                        delete errors[name];
                    }
                }
                break;

            // Validation for Employer Contributions
            case 'fsaEmployerContribution':
                if (plans.FSA?.isFSA) {

                    if (!value) {
                        errors[name] = 'Employer Contribution for FSA is required';
                    } else if (isNaN(value) || parseFloat(value) < 0) {

                        errors[name] = 'Employer Contribution must be a valid positive number';
                    } else {
                        delete errors[name];
                    }
                }
                break;

            case 'dcaEmployerContribution':
                if (plans.DCA?.isDCA) {
                    if (!value) {
                        errors[name] = 'Employer Contribution for DCA is required';
                    } else if (isNaN(value) || parseFloat(value) < 0) {
                        errors[name] = 'Employer Contribution must be a valid positive number';
                    } else {
                        delete errors[name];
                    }
                }
                break;

            case 'lpfsaEmployerContribution':
                if (((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA)) {
                    if (!value) {
                        errors[name] = 'Employer Contribution for LPFSA is required';
                    } else if (isNaN(value) || parseFloat(value) < 0) {
                        errors[name] = 'Employer Contribution must be a valid positive number';
                    } else {
                        delete errors[name];
                    }
                }
                break;

            default:
                break;
        }

        setValidationErrors(errors);
    };

    const validateAll = () => {
        let errors = { ...validationErrors };

        // Validate FSA fields if FSA plan is active
        if (plans.FSA?.isFSA) {
            if (!plans.FSA?.fsaPlanStartDate) {
                errors['fsaPlanStartDate'] = 'FSA plan start date is required';
            } else if (plans.FSA?.fsaPlanEndDate && new Date(plans.FSA?.fsaPlanEndDate) <= new Date(plans.FSA?.fsaPlanStartDate)) {
                errors['fsaPlanEndDate'] = 'FSA plan end date must be after the start date';
            } else {
                delete errors['fsaPlanStartDate'];
                delete errors['fsaPlanEndDate'];
            }

            // if (!plans.FSA?.fsaEmployerContribution || isNaN(parseFloat(plans.FSA?.fsaEmployerContribution)) || parseFloat(plans.FSA?.fsaEmployerContribution) < 0) {
            //     errors['fsaEmployerContribution'] = 'FSA employer contribution is required, must be a number, and cannot be negative';
            // } else {
            //     delete errors['fsaEmployerContribution'];
            // }
        }

        // Validate DCA fields if DCA plan is active
        if (plans.DCA?.isDCA) {
            if (!plans.DCA?.dcaPlanStartDate) {
                errors['dcaPlanStartDate'] = 'DCA plan start date is required';
            } else if (plans.DCA?.dcaPlanEndDate && new Date(plans.DCA?.dcaPlanEndDate) <= new Date(plans.DCA?.dcaPlanStartDate)) {
                errors['dcaPlanEndDate'] = 'DCA plan end date must be after the start date';
            } else {
                delete errors['dcaPlanStartDate'];
                delete errors['dcaPlanEndDate'];
            }

            // if (!plans.DCA?.dcaEmployerContribution || isNaN(parseFloat(plans.DCA?.dcaEmployerContribution)) || parseFloat(plans.DCA?.dcaEmployerContribution) < 0) {
            //     errors['dcaEmployerContribution'] = 'DCA employer contribution is required, must be a number, and cannot be negative';
            // } else {
            //     delete errors['dcaEmployerContribution'];
            // }
        }

        // Validate LPFSA fields if LPFSA plan is active and HSA is offered
        if (((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA)) {
            if (!plans.LPFSA?.lpfsaPlanStartDate) {
                errors['lpfsaPlanStartDate'] = 'LPFSA plan start date is required';
            } else if (plans.LPFSA?.lpfsaPlanEndDate && new Date(plans.LPFSA?.lpfsaPlanEndDate) <= new Date(plans.LPFSA?.lpfsaPlanStartDate)) {
                errors['lpfsaPlanEndDate'] = 'LPFSA plan end date must be after the start date';
            } else {
                delete errors['lpfsaPlanStartDate'];
                delete errors['lpfsaPlanEndDate'];
            }

            // if (!plans.LPFSA?.lpfsaEmployerContribution || isNaN(parseFloat(plans.LPFSA?.lpfsaEmployerContribution)) || parseFloat(plans.LPFSA?.lpfsaEmployerContribution) < 0) {
            //     errors['lpfsaEmployerContribution'] = 'LPFSA employer contribution is required, must be a number, and cannot be negative';
            // } else {
            //     delete errors['lpfsaEmployerContribution'];
            // }
        }



        // Update validation errors state
        setValidationErrors(errors);

        // Return true if no errors, false if there are errors
        return Object.keys(errors).length === 0;
    };









    const handleInputChange = (e, planType, fieldName, LpfsaPlanId) => {
        const { value } = e.target;
        validateField(fieldName, value);

        setPlans((prevPlans) => ({
            ...prevPlans,
            [planType]: {
                ...prevPlans[planType],
                [fieldName]: value,
            },
        }));
        
        
        if(planType==="LPFSA"){
            debouncedUpdate(planType, { [fieldName]: value, ['planId']: LpfsaPlanId });
        }else{
            debouncedUpdate(planType, { [fieldName]: value });
        }

    };

    const handleSelectChange = (selectedOption, planType, fieldName, LpfsaPlanId) => {
        setPlans((prevPlans) => ({
            ...prevPlans,
            [planType]: {
                ...prevPlans[planType],
                [fieldName]: selectedOption ? selectedOption.value : null,  // Update the specific field
            },
        }));

        if(planType==="LPFSA"){
            debouncedUpdate(planType, { [fieldName]: selectedOption ? selectedOption.value : null, ['planId']: LpfsaPlanId });
        }else{
            debouncedUpdate(planType, { [fieldName]: selectedOption ? selectedOption.value : null });
        }

        
        
    };



    
    const handleToggleChange = async (e, planType, fieldName) => {
        const { checked } = e.target;
    
        try {
            setPlans(prev => ({
                ...prev,
                [planType]: {
                    ...prev[planType],
                    [fieldName]: checked
                }
            }));

            if(planType === 'generalInfo'){
                
                debouncedUpdate(
                    planType,
                    { [fieldName]: checked },
                    plans[planType]?.id
                );
            }else{
                debouncedUpdate(
                    planType,
                    { [fieldName]: checked },
                    plans[planType]?.planId
                );
            }
    
            
    
            // special logic
            if (planType === 'generalInfo' && fieldName === 'isHSA' && !checked) {
    
                setPlans(prev => ({
                    ...prev,
                    generalInfo: {
                        ...prev.generalInfo,
                        isOfferLPFSA: false
                    }
                }));
    
                debouncedUpdate(
                    'generalInfo',
                    { isOfferLPFSA: false },
                    plans.generalInfo?.id
                );

                debouncedUpdate(
                    'generalInfo',
                    { isHSA: false },
                    plans.generalInfo?.id
                );
    
                await handleLpfsa({
                    target: { checked: false }
                });
            }
    
        } catch (error) {
            console.error("Toggle error:", error);
        }
    };

    const handleLpfsa = async (e) => {
        const { checked } = e.target;
    
        // Do not add/delete if original LPFSA exists
        if (Object.keys(OriginalLpfsa).length >0) {
            return;
        }
    
        try {
            if (checked) {
                const payload = {
                    planId: uuidv4(),
                    isCurrent: true,
                    isLPFSA: true,
                    caseId: caseId,
                };
    
                const response = await axios.post(
                    `${config.API_URL}/EBLPFSAPlans/`,
                    payload
                );
    
                setPlans((prev) => ({
                    ...prev,
                    LPFSA: response.data || payload,
                }));
    
            } else {
                const planId = plans.LPFSA?.planId;
    
                if (planId) {
                    await axios.delete(
                        `${config.API_URL}/EBLPFSAPlans/${planId}`
                    );
                }
    
                setPlans((prev) => ({
                    ...prev,
                    LPFSA: {},
                }));
            }
    
        } catch (error) {
            console.error("LPFSA error:", error);
        }
    };
    


    const handleDateChange = (planType, fieldName, date, LpfsaPlanId) => {
        if (date) {
            // Normalize the date to remove any time zone offsets
            const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
            // Format the date as MM/dd/yyyy
            const formattedDate = localDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
    
            // Validate the field with the formatted date
            validateField(fieldName, formattedDate);
    
            // Update the state with the formatted date
            setPlans((prevPlans) => ({
                ...prevPlans,
                [planType]: {
                    ...prevPlans[planType],
                    [fieldName]: formattedDate,
                },
            }));
    
            // Send the debounced update to the backend
           
            if(planType==="LPFSA"){
                debouncedUpdate(planType, { [fieldName]: formattedDate, ['planId']: LpfsaPlanId });
            }else{
                debouncedUpdate(planType, { [fieldName]: formattedDate });
            }
        } else {
            // Handle the case when the date is cleared (null)
            setPlans((prevPlans) => ({
                ...prevPlans,
                [planType]: {
                    ...prevPlans[planType],
                    [fieldName]: '', // Clear the field
                },
            }));
    
            // Send the debounced update with an empty value to the backend
            if(planType==="LPFSA"){
                debouncedUpdate(planType, { [fieldName]: '', ['planId']: LpfsaPlanId });
            }else{
                debouncedUpdate(planType, { [fieldName]: '' });
            }
            
        }
    };
    

    const customStyles = (hasError) => ({
        control: (provided) => ({
            ...provided,
            borderColor: hasError ? '#dc3545' : provided.borderColor,
            '&:hover': {
                borderColor: hasError ? '#dc3545' : provided.borderColor,
            },
            boxShadow: hasError ? '0 0 10px rgba(220, 53, 69, 0.5)' : provided.boxShadow,
        }),
    });

    const fsaLenght = Object.keys(plans.FSA).length;
    const dcaLenght = Object.keys(plans.DCA).length;
    const lpfsaLenght = Object.keys(plans.LPFSA).length;
    useImperativeHandle(ref, () => ({
        validateAll,

        fsaLenght,
        dcaLenght,
        lpfsaLenght,

    }));


    useEffect(() => {
        if (plans.FSA?.isFSA) {
            handleDateChange(
                'FSA',
                'fsaPlanTerminationDate',
                null
            );
        } else if (plans.FSA?.fsaPlanStartDate) {
            const startDate = new Date(plans.FSA.fsaPlanStartDate);
    
            const terminationDate = new Date(startDate);
        terminationDate.setDate(
            terminationDate.getDate() - 1
        );
    
            handleDateChange(
                'FSA',
                'fsaPlanTerminationDate',
                terminationDate
            );
        }
    }, [plans.FSA?.isFSA, plans.FSA?.fsaPlanStartDate]);

    useEffect(() => {
        if (plans.DCA?.isDCA) {
            handleDateChange(
                'DCA',
                'dcaPlanTerminationDate',
                null
            );
        } else if (plans.DCA?.dcaPlanStartDate) {
            const startDate = new Date(plans.DCA?.dcaPlanStartDate);
    
            const terminationDate = new Date(startDate);
        terminationDate.setDate(
            terminationDate.getDate() - 1
        );
    
            handleDateChange(
                'DCA',
                'dcaPlanTerminationDate',
                terminationDate
            );
        }
    }, [plans.DCA?.isDCA, plans.DCA?.dcaPlanStartDate]);



    //const isDisabled = renewalCase?.ebSubmissionStatus === 'Submitted' || isDisableForUsers;
    const isDisabled =  isDisableForUsers;

    if (loading) return <Skeleton />;
    if (error) return <div>{error}</div>;



    return (
        <>
            
                <Col lg={12} className='mb-3'>
                    <Card>
                        <Card.Body>
                            <h5 className="card-title">Flexible Spending Account (FSA)</h5>
                            
                                <Row className='mb-2'>
                                    <Col lg={12}>
                                        <Form.Group controlId="plan_IsDisabilityExtension" className="d-flex">
                                            <Col lg={5}>
                                                <div className="d-flex align-items-center">
                                                    <Form.Label>
                                                        Do you have a Health Savings Account (HSA)?
                                                    </Form.Label>
                                                    <div className="form-check form-switch form-toggle">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input form-check-input-toggel toggle-input-switch"
                                                            role="switch"
                                                            checked={plans.generalInfo?.isHSA || false}
                                                            onChange={(e) => handleToggleChange(e, 'generalInfo', 'isHSA', plans.generalInfo?.id )}
                                                            disabled={isDisabled}
                                                        />
                                                    </div>
                                                </div>

                                            </Col>

                                        </Form.Group>
                                    </Col>
                                </Row>
                            

                            {(plans.generalInfo?.isHSA) && (

                                <Row className='mb-2'>
                                    <Col lg={12}>
                                        <Form.Group controlId="plan_IsDisabilityExtension" className="d-flex">
                                            <Col lg={6}>
                                                <div className="d-flex align-items-center">
                                                    <Form.Label>
                                                        Do you want to offer a Limited Purpose FSA (LPFSA)?
                                                    </Form.Label>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id={`tooltip-plan-rule-`}>
                                                                A Limited-Purpose Healthcare FSA works alongside plans that offer Health Savings Accounts (HSAs). Employees can elect a Limited-Purpose FSA for out-of-pocket dental and vision expenses while maintaining eligibility to contribute to an HSA. Employees preserve their HSA funds for investment and future financial health.
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                            <i className="dropicons dripicons-information"></i>
                                                        </span>
                                                    </OverlayTrigger>
                                                    <div className="form-check form-switch form-toggle">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input form-check-input-toggel toggle-input-switch"
                                                            role="switch"
                                                            checked={plans.generalInfo?.isOfferLPFSA || false}
                                                            onChange={(e) => { handleLpfsa(e); handleToggleChange(e, 'generalInfo', 'isOfferLPFSA', plans.generalInfo?.id);  }}
                                                            disabled={isDisabled}
                                                        />
                                                    </div>
                                                </div>
                                            </Col>

                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}
                            <Row>
                                <Col lg={12}>
                                {informationProvider === "ADP Marketplace"? (
                                    <Row className='mb-3'>
                                    <Col lg={3}>
                                                <Form.Label>
                                                ADP PAYROLL CODE
                                                </Form.Label>
                                                <OverlayTrigger
                                                            placement="top"
                                                            overlay={
                                                                <Tooltip id={`tooltip-plan-rule-`}>
                                                                    Please provide the ADP Payroll Code for this plan
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                                <i className="dropicons dripicons-information"></i>
                                                            </span>
                                                        </OverlayTrigger>
                                            </Col>
                                    <Col lg={3}>
                                                    <InputGroup className="mb-3">
                                                        <Form.Control
                                                            type="text"
                                                            id="fsaAdpPayrollCode"
                                                            name="fsaAdpPayrollCode"
                                                            isInvalid={!!validationErrors.fsaAdpPayrollCode}
                                                            placeholder="ADP Payroll Code"
                                                            value={plans.FSA?.fsaAdpPayrollCode || ''}
                                                            onChange={(e) => handleInputChange(e, 'FSA', 'fsaAdpPayrollCode')}
                                                            title="ADP Payroll Code"
                                                            disabled={isDisabled}
                                                        />
                                                        {validationErrors.fsaAdpPayrollCode && (
                                                            <div className="invalid-feedback d-block">
                                                                {validationErrors.fsaAdpPayrollCode}
                                                            </div>
                                                        )}
                                                    </InputGroup>
    
                                                </Col>
                                                <Col lg={3}>
                                                <InputGroup className="mb-3">
                                                        <Form.Control
                                                            type="text"
                                                            id="dcaAdpPayrollCode"
                                                            name="dcaAdpPayrollCode"
                                                            isInvalid={!!validationErrors.dcaAdpPayrollCode}
                                                            placeholder="ADP Payroll Code"
                                                            value={plans.DCA?.dcaAdpPayrollCode || ''}
                                                            onChange={(e) => handleInputChange(e, 'DCA', 'dcaAdpPayrollCode')}
                                                            title="ADP Payroll Code"
                                                            disabled={isDisabled}
                                                        />
                                                        {validationErrors.dcaAdpPayrollCode && (
                                                            <div className="invalid-feedback d-block">
                                                                {validationErrors.dcaAdpPayrollCode}
                                                            </div>
                                                        )}
                                                    </InputGroup>
    
                                                </Col>
                                                
                                                {((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                                <Col lg={3}>
                                                <InputGroup className="mb-3">
                                                        <Form.Control
                                                            type="text"
                                                            id="lpfsaAdpPayrollCode"
                                                            name="lpfsaAdpPayrollCode"
                                                            isInvalid={!!validationErrors.lpfsaAdpPayrollCode}
                                                            placeholder="ADP Payroll Code"
                                                            value={plans.FSA?.lpfsaAdpPayrollCode || ''}
                                                            onChange={(e) => handleInputChange(e, 'LPFSA', 'lpfsaAdpPayrollCode')}
                                                            title="ADP Payroll Code"
                                                            disabled={isDisabled}
                                                        />
                                                        {validationErrors.lpfsaAdpPayrollCode && (
                                                            <div className="invalid-feedback d-block">
                                                                {validationErrors.lpfsaAdpPayrollCode}
                                                            </div>
                                                        )}
                                                    </InputGroup>
    
                                                </Col>) : (<Col lg={3} />)}
                                    </Row>
                                ):('')}

                                

                                
                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                                Select the plan
                                            </Form.Label>
                                        </Col>
                                        <Col lg={3}>
                                            <Form.Group className=''>

                                                <input role="checkbox" type="checkbox" value="true" className="form-check-input checkbox-input"
                                                    checked={plans.FSA?.isFSA || false}
                                                    onChange={(e) => handleToggleChange(e, 'FSA', 'isFSA', plans.FSA?.planId )} disabled={isDisabled} />
                                                <label className="form-check-label ms-1" htmlFor="dca_check">Flexible Spending Account (FSA)</label>
                                            </Form.Group>
                                        </Col>
                                        <Col lg={3}>
                                            <Form.Group className=''>

                                                <input role="checkbox" type="checkbox" value="true" className="form-check-input checkbox-input"
                                                    checked={plans.DCA?.isDCA || false}
                                                    onChange={(e) => handleToggleChange(e, 'DCA', 'isDCA', plans.DCA?.planId)} disabled={isDisabled} />
                                                <label className="form-check-label ms-1" htmlFor="dca_check">Dependent Care (DCA)</label>
                                            </Form.Group>
                                        </Col>
                                        {(((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) || Object.keys(OriginalLpfsa).length >0) ? (
                                            <Col lg={3}>
                                                <Form.Group className=''>

                                                    {/* <input role="checkbox" type="checkbox" value="true" className="form-check-input checkbox-input"
                                                        checked={plans.LPFSA?.isLPFSA || false}
                                                        onChange={(e) => handleToggleChange(e, 'LPFSA', 'isLPFSA')} disabled={isDisabled} /> */}
                                                    <label className="form-check-label ms-1" htmlFor="lpfsa_check">Limited Purpose FSA (LPFSA) </label>
                                                </Form.Group>
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>
                                    {(!plans.FSA?.isFSA || !plans.DCA?.isDCA || !plans.generalInfo?.isOfferLPFSA) ? (
                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                                Termination Date
                                            </Form.Label>
                                        </Col>
                                        {!plans.FSA?.isFSA ? (
                                            <Col lg={3}>
                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>

                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.fsaPlanTerminationDate ? 'is-invalid' : ''}`}
                                                        selected={
                                                            plans.FSA?.fsaPlanTerminationDate
                                                                ? new Date(
                                                                      plans.FSA.fsaPlanTerminationDate
                                                                  )
                                                                : null
                                                        }
                                                        onChange={(date) => handleDateChange('FSA', 'fsaPlanTerminationDate', date)}
                                                        placeholderText="Plan Termination Date" disabled={isDisabled} minDate={
                                                            plans.FSA?.fsaPlanStartDate
                                                                ? new Date(
                                                                      new Date(
                                                                          plans.FSA.fsaPlanStartDate
                                                                      ).setDate(
                                                                          new Date(
                                                                              plans.FSA.fsaPlanStartDate
                                                                          ).getDate() - 1
                                                                      )
                                                                  )
                                                                : null
                                                        } dateFormat="MM/dd/yyyy"
                                                    />
                                                </div>
                                                {validationErrors.fsaPlanTerminationDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.fsaPlanStartDate}
                                                    </div>
                                                )}
                                            </Col>) : (<Col lg={3} />)}
                                        {!plans.DCA?.isDCA ? (
                                            <Col lg={3}>
                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>
                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.dcaPlanTerminationDate ? 'is-invalid' : ''}`}
                                                        
                                                        selected={
                                                            plans.DCA?.dcaPlanTerminationDate
                                                                ? new Date(
                                                                    plans.DCA.dcaPlanTerminationDate
                                                                  )
                                                                : null
                                                        }
                                                        onChange={(date) => handleDateChange('DCA', 'dcaPlanTerminationDate', date)}
                                                        placeholderText="Plan Termination Date" disabled={isDisabled} minDate={
                                                            plans.DCA?.dcaPlanStartDate
                                                                ? new Date(
                                                                      new Date(
                                                                          plans.DCA.dcaPlanStartDate
                                                                      ).setDate(
                                                                          new Date(
                                                                            plans.DCA.dcaPlanStartDate
                                                                          ).getDate() - 1
                                                                      )
                                                                  )
                                                                : null
                                                        } dateFormat="MM/dd/yyyy"
                                                    />
                                                </div>
                                                {validationErrors.dcaPlanTerminationDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.dcaPlanTerminationDate}
                                                    </div>
                                                )}
                                            </Col>) : (<Col lg={3} />)}
                                            {(!plans.generalInfo?.isOfferLPFSA && Object.keys(OriginalLpfsa).length >0) ? (
                                            <Col lg={3}>
                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>
                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.lpfsaPlanTerminationDate ? 'is-invalid' : ''}`}
                                                        selected={plans.LPFSA?.lpfsaPlanTerminationDate || ''}
                                                        onChange={(date) => handleDateChange('LPFSA', 'lpfsaPlanTerminationDate', date, plans.LPFSA?.planId)}
                                                        placeholderText="Plan Termination Date" disabled={isDisabled} minDate={new Date()}
                                                    />
                                                </div>
                                                {validationErrors.lpfsaPlanTerminationDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.lpfsaPlanTerminationDate}
                                                    </div>
                                                )}
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>
                                    ):('')}
                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                                Plan Start Date
                                            </Form.Label>
                                        </Col>
                                        {plans.FSA?.isFSA ? (
                                            <Col lg={3}>
                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>

                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.fsaPlanStartDate ? 'is-invalid' : ''}`}
                                                        selected={plans.FSA?.fsaPlanStartDate || ''}
                                                        onChange={(date) => handleDateChange('FSA', 'fsaPlanStartDate', date)}
                                                        placeholderText="Plan Start Date" disabled={isDisabled}
                                                    />
                                                </div>
                                                {validationErrors.fsaPlanStartDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.fsaPlanStartDate}
                                                    </div>
                                                )}
                                            </Col>) : (<Col lg={3} />)}
                                        {plans.DCA?.isDCA ? (
                                            <Col lg={3}>
                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>
                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.dcaPlanStartDate ? 'is-invalid' : ''}`}
                                                        selected={plans.DCA?.dcaPlanStartDate || ''}
                                                        onChange={(date) => handleDateChange('DCA', 'dcaPlanStartDate', date)}
                                                        placeholderText="Plan Start Date" disabled={isDisabled}
                                                    />
                                                </div>
                                                {validationErrors.dcaPlanStartDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.dcaPlanStartDate}
                                                    </div>
                                                )}
                                            </Col>) : (<Col lg={3} />)}
                                        {((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                            <Col lg={3}>
                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>
                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.lpfsaPlanStartDate ? 'is-invalid' : ''}`}
                                                        selected={plans.LPFSA?.lpfsaPlanStartDate || ''}
                                                        onChange={(date) => handleDateChange('LPFSA', 'lpfsaPlanStartDate', date, plans.LPFSA?.planId)}
                                                        placeholderText="Plan Start Date" disabled={isDisabled}
                                                    />
                                                </div>
                                                {validationErrors.lpfsaPlanStartDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.lpfsaPlanStartDate}
                                                    </div>
                                                )}
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>
                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                                Plan End Date
                                            </Form.Label>
                                        </Col>
                                        {plans.FSA?.isFSA ? (
                                            <Col lg={3}>
                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>

                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.fsaPlanEndDate ? 'is-invalid' : ''}`}
                                                        selected={plans.FSA?.fsaPlanEndDate || ''}
                                                        onChange={(date) => handleDateChange('FSA', 'fsaPlanEndDate', date)}
                                                        placeholderText="Plan End Date" disabled={isDisabled}
                                                        minDate={plans.FSA?.fsaPlanStartDate ? new Date(plans.FSA?.fsaPlanStartDate) : null}
                                                    />

                                                </div>
                                                {validationErrors.fsaPlanEndDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.fsaPlanEndDate}
                                                    </div>
                                                )}
                                            </Col>) : (<Col lg={3} />)}
                                        {plans.DCA?.isDCA ? (
                                            <Col lg={3}>
                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>

                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.dcaPlanEndDate ? 'is-invalid' : ''}`}
                                                        selected={plans.DCA?.dcaPlanEndDate || ''}
                                                        onChange={(date) => handleDateChange('DCA', 'dcaPlanEndDate', date)}
                                                        placeholderText="Plan End Date" disabled={isDisabled}
                                                        minDate={plans.DCA?.dcaPlanStartDate ? new Date(plans.DCA?.dcaPlanStartDate) : null}
                                                    />
                                                </div>
                                                {validationErrors.dcaPlanEndDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.dcaPlanEndDate}
                                                    </div>
                                                )}
                                            </Col>) : (<Col lg={3} />)}
                                        {((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                            <Col lg={3}>
                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>

                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.lpfsaPlanEndDate ? 'is-invalid' : ''}`}
                                                        selected={plans.LPFSA?.lpfsaPlanEndDate || ''}
                                                        onChange={(date) => handleDateChange('LPFSA', 'lpfsaPlanEndDate', date, plans.LPFSA?.planId)}
                                                        placeholderText="Plan End Date" disabled={isDisabled}
                                                        minDate={plans.LPFSA?.lpfsaPlanStartDate ? new Date(plans.LPFSA?.lpfsaPlanStartDate) : null}
                                                    />
                                                </div>
                                                {validationErrors.lpfsaPlanEndDate && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.lpfsaPlanEndDate}
                                                    </div>
                                                )}
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>
                                    <Row>
                                        <Col lg={3}>
                                            <Form.Label>
                                                Annual Election Amount
                                            </Form.Label>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`tooltip-min`}>
                                                        Clarity will automatically increase the annual maximum based on the IRS maximum</Tooltip>
                                                }
                                            >
                                                <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                    <i className="dropicons dripicons-information"></i>
                                                </span>
                                            </OverlayTrigger>
                                        </Col>
                                        {plans.FSA?.isFSA ? (
                                            <Col lg={3}>
                                                <MinMaxInput
                                                    minValue={plans.FSA?.minFSAAnnualElectionAmount === undefined ? '' : plans.FSA?.minFSAAnnualElectionAmount}
                                                    maxValue={plans.FSA?.maxFSAAnnualElectionAmount === undefined ? '' : plans.FSA?.maxFSAAnnualElectionAmount}
                                                    minPlaceholder="$0.00 Min"
                                                    maxPlaceholder="$3,400.00 Max"
                                                    minValidation={{ min: 0, max: 3399 }}
                                                    maxValidation={{ min: 0, max: 3400 }}
                                                    updateMin={(value) => handleInputChange({ target: { value } }, 'FSA', 'minFSAAnnualElectionAmount')}
                                                    updateMax={(value) => handleInputChange({ target: { value } }, 'FSA', 'maxFSAAnnualElectionAmount')}
                                                    tooltip="Clarity will automatically increase the annual maximum based on the IRS maximum"
                                                    disabled={isDisabled}
                                                />



                                            </Col>) : (
                                            <Col lg={3} />
                                        )}

                                        {plans.DCA?.isDCA ? (
                                            <Col lg={3}>
                                                <MinMaxInput
                                                    minValue={plans.DCA?.minDCAAnnualElectionAmount === undefined ? '' : plans.DCA?.minDCAAnnualElectionAmount}
                                                    maxValue={plans.DCA?.maxDCAAnnualElectionAmount === undefined ? '' : plans.DCA?.maxDCAAnnualElectionAmount}

                                                    minPlaceholder="$0.00 Min"
                                                    maxPlaceholder="$7,500.00 Max"
                                                    minValidation={{ min: 0, max: 7499 }}
                                                    maxValidation={{ min: 0, max: 7500 }}
                                                    updateMin={(value) => handleInputChange({ target: { value } }, 'DCA', 'minDCAAnnualElectionAmount')}
                                                    updateMax={(value) => handleInputChange({ target: { value } }, 'DCA', 'maxDCAAnnualElectionAmount')}
                                                    tooltip="Clarity will automatically increase the annual maximum based on the IRS maximum"
                                                    disabled={isDisabled}
                                                />
                                            </Col>
                                        ) : (
                                            <Col lg={3} />
                                        )}

                                        {((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                            <Col lg={3}>
                                                <MinMaxInput
                                                    minValue={plans.LPFSA?.minLPFSAAnnualElectionAmount === undefined ? '' : plans.LPFSA?.minLPFSAAnnualElectionAmount}
                                                    maxValue={plans.LPFSA?.maxLPFSAAnnualElectionAmount === undefined ? '' : plans.LPFSA?.maxLPFSAAnnualElectionAmount}

                                                    minPlaceholder="$0.00 Min"
                                                    maxPlaceholder="$3,400.00 Max"
                                                    minValidation={{ min: 0, max: 3399 }}
                                                    maxValidation={{ min: 0, max: 3400 }}
                                                    updateMin={(value) => handleInputChange({ target: { value } }, 'LPFSA', 'minLPFSAAnnualElectionAmount', plans.LPFSA?.planId)}
                                                    updateMax={(value) => handleInputChange({ target: { value } }, 'LPFSA', 'maxLPFSAAnnualElectionAmount', plans.LPFSA?.planId)}
                                                    tooltip="Clarity will automatically increase the annual maximum based on the IRS maximum"
                                                    disabled={isDisabled}
                                                />
                                            </Col>
                                        ) : (
                                            <Col lg={3} />
                                        )}
                                    </Row>

                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                            Employer Contribution

                                            </Form.Label>
                                            
                                        </Col>
                                        {plans.FSA?.isFSA ? (
                                            <Col lg={3}>

                                                <div className="form-check form-switch form-toggle">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                        role="switch"
                                                        checked={plans.FSA?.fsaIsEmployerContribution || false}
                                                        onChange={(e) => handleToggleChange(e, 'FSA', 'fsaIsEmployerContribution')}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}
                                            {plans.DCA?.isDCA ? (
                                            <Col lg={3}>

                                                <div className="form-check form-switch form-toggle">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                        role="switch"
                                                        checked={plans.DCA?.dcaIsEmployerContribution || false}
                                                        onChange={(e) => handleToggleChange(e, 'DCA', 'dcaIsEmployerContribution')}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}

                                        {((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                            <Col lg={3}>

                                                <div className="form-check form-switch form-toggle">
                                                    <input
                                                        type="checkbox" 
                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                        role="switch"
                                                        checked={plans.LPFSA?.lpfsaIsEmployerContribution || false}
                                                        onChange={(e) => handleToggleChange(e, 'LPFSA', 'lpfsaIsEmployerContribution', plans.LPFSA?.planId)}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>

                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                            The employer contribution is
                                            </Form.Label>
                                        </Col>
                                        {plans.FSA?.isFSA && plans.FSA?.fsaIsEmployerContribution  ? (
                                            <Col lg={3}>

                                                <Select
                                                    name="fsaEmployerContributionType"
                                                    placeholder="Please select"
                                                    options={employerContributionOptions}
                                                    value={employerContributionOptions.find(option => option.value === String(plans.FSA?.fsaEmployerContributionType))}
                                                    styles={customStyles(validationErrors && validationErrors.fsaEmployerContributionType)}
                                                    isDisabled={isDisabled}
                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, 'FSA', 'fsaEmployerContributionType')}
                                                />
                                            </Col>) : (<Col lg={3} />)}
                                        {plans.DCA?.isDCA && plans.DCA?.dcaIsEmployerContribution ? (
                                            <Col lg={3}>

                                                <Select
                                                    name="dcaEmployerContributionType"
                                                    placeholder="Please select"
                                                    options={employerContributionDcaOptions}
                                                    value={employerContributionDcaOptions.find(option => option.value === String(plans.DCA?.dcaEmployerContributionType))}
                                                    styles={customStyles(validationErrors && validationErrors.dcaEmployerContributionType)}
                                                    isDisabled={isDisabled}
                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, 'DCA', 'dcaEmployerContributionType')}
                                                />
                                            </Col>) : (<Col lg={3} />)}
                                        {(((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) && plans.LPFSA?.lpfsaIsEmployerContribution) ? (
                                            <Col lg={3}>

                                                <Select
                                                    name="lpfsaEmployerContributionType"
                                                    placeholder="Please select"
                                                    options={employerContributionOptions}
                                                    value={employerContributionOptions.find(option => option.value === String(plans.LPFSA?.lpfsaEmployerContributionType))}
                                                    styles={customStyles(validationErrors && validationErrors.lpfsaEmployerContributionType)}
                                                    isDisabled={isDisabled}
                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, 'LPFSA', 'lpfsaEmployerContributionType', plans.LPFSA?.planId)}
                                                />
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>

                                    <Row className='mb-3'>
                                    <Col lg={3}>
                                                <Form.Label>
                                                
                                                </Form.Label>
                                                
                                            </Col>
                                            {plans.FSA?.isFSA && plans.FSA?.fsaIsEmployerContribution &&  plans.FSA?.fsaIsEmployerContribution
                                              ? (
                                    <Col lg={3}>
                                        {plans.FSA?.fsaEmployerContributionType === 'Combination of flat amount and dollar for dollar match' ?
                                        (<>
                                        <InputGroup className="">
                                                        <Form.Control
                                                            type="number"
                                                            id="fsaCombinationFlatAmount"
                                                            name="fsaCombinationFlatAmount"
                                                            isInvalid={!!validationErrors.fsaCombinationFlatAmount}
                                                            placeholder="Enter combo flat amount"
                                                            value={plans.FSA?.fsaCombinationFlatAmount || ''}
                                                            onChange={(e) => handleInputChange(e, 'FSA', 'fsaCombinationFlatAmount')}
                                                            title="Enter combo flat amount"
                                                            disabled={isDisabled}
                                                        />
                                                        {validationErrors.fsaCombinationFlatAmount && (
                                                            <div className="invalid-feedback d-block">
                                                                {validationErrors.fsaCombinationFlatAmount}
                                                            </div>
                                                        )}
                                                    </InputGroup>
                                                    <InputGroup className="mb-3">
                                                        <Form.Control
                                                            type="number"
                                                            id="fsaCombinationMaxAmount"
                                                            name="fsaCombinationMaxAmount"
                                                            isInvalid={!!validationErrors.fsaCombinationMaxAmount}
                                                            placeholder="Enter combo max match"
                                                            value={plans.FSA?.fsaCombinationMaxAmount || ''}
                                                            onChange={(e) => handleInputChange(e, 'FSA', 'fsaCombinationMaxAmount')}
                                                            title="Enter combo max match"
                                                            disabled={isDisabled}
                                                        />
                                                        {validationErrors.fsaCombinationMaxAmount && (
                                                            <div className="invalid-feedback d-block">
                                                                {validationErrors.fsaCombinationMaxAmount}
                                                            </div>
                                                        )}
                                                    </InputGroup>
                                        </>): plans.FSA?.fsaEmployerContributionType === 'A flat contribution for all up to a maximum of $500' ? 
                                        (<InputGroup className="mb-3">
                                            <Form.Control
                                                type="number"
                                                id="fsaFlatContributionAmount"
                                                name="fsaFlatContributionAmount"
                                                isInvalid={!!validationErrors.fsaFlatContributionAmount}
                                                placeholder="Enter flat amount"
                                                value={plans.FSA?.fsaFlatContributionAmount || ''}
                                                onChange={(e) => handleInputChange(e, 'FSA', 'fsaFlatContributionAmount')}
                                                title="Enter flat amount"
                                                disabled={isDisabled}
                                            />
                                            {validationErrors.fsaFlatContributionAmount && (
                                                <div className="invalid-feedback d-block">
                                                    {validationErrors.fsaFlatContributionAmount}
                                                </div>
                                            )}
                                        </InputGroup>): plans.FSA?.fsaEmployerContributionType === 'A dollar for dollar match' ?
                                        (<InputGroup className="mb-3">
                                            <Form.Control
                                            type="number"
                                            id="fsaDollarAmount"
                                            name="fsaDollarAmount"
                                            isInvalid={!!validationErrors.fsaDollarAmount}
                                            placeholder="Enter max match amount"
                                            value={plans.FSA?.fsaDollarAmount || ''}
                                            onChange={(e) => handleInputChange(e, 'FSA', 'fsaDollarAmount')}
                                            title="Enter max match amount"
                                            disabled={isDisabled}
                                        />
                                        {validationErrors.fsaDollarAmount && (
                                            <div className="invalid-feedback d-block">
                                                {validationErrors.fsaDollarAmount}
                                            </div>
                                        )}
                                        </InputGroup>):('')}


                                                    
    
                                                    </Col>) : (<Col lg={3} />)}
                                                
                                                
                                                
                                                <Col lg={3}>
                                                
    
                                                </Col>
                                                
                                                {((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) && plans.LPFSA?.lpfsaIsEmployerContribution ? (
                                                <Col lg={3}>
                                                {plans.LPFSA?.lpfsaEmployerContributionType === 'Combination of flat amount and dollar for dollar match' ?
                                                (<>
                                                <InputGroup className="">
                                                                <Form.Control
                                                                    type="number"
                                                                    id="lpfsaCombinationFlatAmount"
                                                                    name="lpfsaCombinationFlatAmount"
                                                                    isInvalid={!!validationErrors.lpfsaCombinationFlatAmount}
                                                                    placeholder="Enter combo flat amount"
                                                                    value={plans.LPFSA?.lpfsaCombinationFlatAmount || ''}
                                                                    onChange={(e) => handleInputChange(e, 'LPFSA', 'lpfsaCombinationFlatAmount', plans.LPFSA?.planId)}
                                                                    title="Enter combo flat amount"
                                                                    disabled={isDisabled}
                                                                />
                                                                {validationErrors.lpfsaCombinationFlatAmount && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {validationErrors.lpfsaCombinationFlatAmount}
                                                                    </div>
                                                                )}
                                                            </InputGroup>
                                                            <InputGroup className="mb-3">
                                                                <Form.Control
                                                                    type="number"
                                                                    id="lpfsaCombinationMaxAmount"
                                                                    name="lpfsaCombinationMaxAmount"
                                                                    isInvalid={!!validationErrors.lpfsaCombinationMaxAmount}
                                                                    placeholder="Enter combo max match"
                                                                    value={plans.LPFSA?.lpfsaCombinationMaxAmount || ''}
                                                                    onChange={(e) => handleInputChange(e, 'LPFSA', 'lpfsaCombinationMaxAmount', plans.LPFSA?.planId)}
                                                                    title="Enter combo max match"
                                                                    disabled={isDisabled}
                                                                />
                                                                {validationErrors.lpfsaCombinationMaxAmount && (
                                                                    <div className="invalid-feedback d-block">
                                                                        {validationErrors.lpfsaCombinationMaxAmount}
                                                                    </div>
                                                                )}
                                                            </InputGroup>
                                                </>): plans.LPFSA?.lpfsaEmployerContributionType === 'A flat contribution for all up to a maximum of $500' ? 
                                                (<InputGroup className="mb-3">
                                                    <Form.Control
                                                        type="number"
                                                        id="lpfsaFlatContributionAmount"
                                                        name="lpfsaFlatContributionAmount"
                                                        isInvalid={!!validationErrors.lpfsaFlatContributionAmount}
                                                        placeholder="Enter flat amount"
                                                        value={plans.LPFSA?.lpfsaFlatContributionAmount || ''}
                                                        onChange={(e) => handleInputChange(e, 'LPFSA', 'lpfsaFlatContributionAmount', plans.LPFSA?.planId)}
                                                        title="Enter flat amount"
                                                        disabled={isDisabled}
                                                    />
                                                    {validationErrors.lpfsaFlatContributionAmount && (
                                                        <div className="invalid-feedback d-block">
                                                            {validationErrors.lpfsaFlatContributionAmount}
                                                        </div>
                                                    )}
                                                </InputGroup>): plans.LPFSA?.lpfsaEmployerContributionType === 'A dollar for dollar match' ?
                                                (<InputGroup className="mb-3">
                                                    <Form.Control
                                                    type="number"
                                                    id="lpfsaDollarAmount"
                                                    name="lpfsaDollarAmount"
                                                    isInvalid={!!validationErrors.lpfsaDollarAmount}
                                                    placeholder="Enter max match amount"
                                                    value={plans.LPFSA?.lpfsaDollarAmount || ''}
                                                    onChange={(e) => handleInputChange(e, 'LPFSA', 'lpfsaDollarAmount', plans.LPFSA?.planId)}
                                                    title="Enter max match amount"
                                                    disabled={isDisabled}
                                                />
                                                {validationErrors.lpfsaDollarAmount && (
                                                    <div className="invalid-feedback d-block">
                                                        {validationErrors.lpfsaDollarAmount}
                                                    </div>
                                                )}
                                                </InputGroup>):('')}
        
        
                                                            
            
                                                            </Col>) : (<Col lg={3} />)}
                                    </Row>

                                    {informationProvider === "ADP Marketplace"? (
                                        <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                           <strong>ADP Marketplace cannot send EMPLOYER contributions.</strong> How will you post employer contributions?
                                            </Form.Label>
                                        </Col>
                                        {plans.FSA?.isFSA && plans.FSA?.fsaIsEmployerContribution  ? (
                                            <Col lg={3}>

                                                <Select
                                                    name="fsaEmployerContributionMethod"
                                                    placeholder="Please select"
                                                    options={EmployerContributionMethods}
                                                    value={EmployerContributionMethods.find(option => option.value === String(plans.FSA?.fsaEmployerContributionMethod))}
                                                    styles={customStyles(validationErrors && validationErrors.fsaEmployerContributionMethod)}
                                                    isDisabled={isDisabled}
                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, 'FSA', 'fsaEmployerContributionMethod')}
                                                />
                                            </Col>) : (<Col lg={3} />)}
                                        {plans.DCA?.isDCA && plans.DCA?.dcaIsEmployerContribution ? (
                                            <Col lg={3}>

                                                <Select
                                                    name="dcaEmployerContributionMethod"
                                                    placeholder="Please select"
                                                    options={EmployerContributionMethods}
                                                    value={EmployerContributionMethods.find(option => option.value === String(plans.DCA?.dcaEmployerContributionMethod))}
                                                    styles={customStyles(validationErrors && validationErrors.dcaEmployerContributionMethod)}
                                                    isDisabled={isDisabled}
                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, 'DCA', 'dcaEmployerContributionMethod')}
                                                />
                                            </Col>) : (<Col lg={3} />)}
                                        {(((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) && plans.LPFSA?.lpfsaIsEmployerContribution) ? (
                                            <Col lg={3}>

                                                <Select
                                                    name="lpfsaEmployerContributionMethod"
                                                    placeholder="Please select"
                                                    options={EmployerContributionMethods}
                                                    value={EmployerContributionMethods.find(option => option.value === String(plans.LPFSA?.lpfsaEmployerContributionMethod))}
                                                    styles={customStyles(validationErrors && validationErrors.lpfsaEmployerContributionMethod)}
                                                    isDisabled={isDisabled}
                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, 'LPFSA', 'lpfsaEmployerContributionMethode', plans.LPFSA?.planId)}
                                                />
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>
                                    ):('')}

                                    

                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                                Claims Runout Period for Active Employees
                                            </Form.Label>
                                        </Col>
                                        {plans.FSA?.isFSA ? (
                                            <Col lg={3}>


                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>

                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.fsaClaimsRunoutActiveEmp ? 'is-invalid' : ''}`}
                                                        selected={plans.FSA?.fsaClaimsRunoutActiveEmp || ''}
                                                        onChange={(date) => handleDateChange('FSA', 'fsaClaimsRunoutActiveEmp', date)}
                                                        placeholderText="Please select date"
                                                        minDate={new Date()}
                                                        disabled={isDisabled}
                                                    />
                                                </div>

                                            </Col>) : (<Col lg={3} />)}
                                        {plans.DCA?.isDCA ? (
                                            <Col lg={3}>

                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>

                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.dcaClaimsRunoutActiveEmp ? 'is-invalid' : ''}`}
                                                        selected={plans.DCA?.dcaClaimsRunoutActiveEmp || ''}
                                                        onChange={(date) => handleDateChange('DCA', 'dcaClaimsRunoutActiveEmp', date)}
                                                        placeholderText="Please select date"
                                                        minDate={new Date()}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}
                                        {((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                            <Col lg={3}>

                                                <div className="d-flex ">
                                                    <span className="input-group-text">
                                                        <i className="  far fa-calendar-alt"></i>
                                                    </span>

                                                    <DatePicker
                                                        className={`form-control ${validationErrors && validationErrors.lpfsaClaimsRunoutActiveEmp ? 'is-invalid' : ''}`}
                                                        selected={plans.LPFSA?.lpfsaClaimsRunoutActiveEmp || ''}
                                                        onChange={(date) => handleDateChange('LPFSA', 'lpfsaClaimsRunoutActiveEmp', date, plans.LPFSA?.planId)}
                                                        placeholderText="Please select date"
                                                        minDate={new Date()}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>
                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                                Claims Runout Period for Terminated Employees
                                            </Form.Label>
                                        </Col>
                                        {plans.FSA?.isFSA ? (
                                            <Col lg={3}>

                                                <Select
                                                    name="fsaClaimsRunoutTerminatedEmp"
                                                    placeholder="Please select"
                                                    options={options}
                                                    value={options.find(option => option.value === String(plans.FSA?.fsaClaimsRunoutTerminatedEmp))}
                                                    styles={customStyles(validationErrors && validationErrors.fsaClaimsRunoutTerminatedEmp)}
                                                    isDisabled={isDisabled}
                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, 'FSA', 'fsaClaimsRunoutTerminatedEmp')}
                                                />
                                            </Col>) : (<Col lg={3} />)}
                                        {plans.DCA?.isDCA ? (
                                            <Col lg={3}>

                                                <Select
                                                    name="dcaClaimsRunoutTerminatedEmp"
                                                    placeholder="Please select"
                                                    options={options}
                                                    value={options.find(option => option.value === String(plans.DCA?.dcaClaimsRunoutTerminatedEmp))}
                                                    styles={customStyles(validationErrors && validationErrors.dcaClaimsRunoutTerminatedEmp)}
                                                    isDisabled={isDisabled}
                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, 'DCA', 'dcaClaimsRunoutTerminatedEmp')}
                                                />
                                            </Col>) : (<Col lg={3} />)}
                                        {((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                            <Col lg={3}>

                                                <Select
                                                    name="lpfsaClaimsRunoutTerminatedEmp"
                                                    placeholder="Please select"
                                                    options={options}
                                                    value={options.find(option => option.value === String(plans.LPFSA?.lpfsaClaimsRunoutTerminatedEmp))}
                                                    styles={customStyles(validationErrors && validationErrors.lpfsaClaimsRunoutTerminatedEmp)}
                                                    isDisabled={isDisabled}
                                                    onChange={(selectedOption) => handleSelectChange(selectedOption, 'LPFSA', 'lpfsaClaimsRunoutTerminatedEmp', plans.LPFSA?.planId)}
                                                />
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>
                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                            Enable Carry Over
                                            </Form.Label>
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`tooltip-plan-rule-`}>
                                                        Allows active participants to carry over unused funds at the end of the plan year (up to the IRS maximum).                                            </Tooltip>
                                                }
                                            >
                                                <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                    <i className="dropicons dripicons-information"></i>
                                                </span>
                                            </OverlayTrigger>
                                        </Col>
                                        {plans.FSA?.isFSA ? (
                                            <Col lg={3}>

                                                <div className="form-check form-switch form-toggle">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                        role="switch"
                                                        checked={plans.FSA?.isFSAAllowCarryOver || false}
                                                        onChange={(e) => handleToggleChange(e, 'FSA', 'isFSAAllowCarryOver')}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}
                                        <Col lg={3}></Col>

                                        {((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                            <Col lg={3}>

                                                <div className="form-check form-switch form-toggle">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                        role="switch"
                                                        checked={plans.LPFSA?.isLPFSAAllowCarryOver || false}
                                                        onChange={(e) => handleToggleChange(e, 'LPFSA', 'isLPFSAAllowCarryOver', plans.LPFSA?.planId)}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>

                                    <Row className='mb-3'>
                                        <Col lg={3}>
                                            <Form.Label>
                                            Enable Grace Period
                                            </Form.Label>
                                        </Col>
                                        {!plans.FSA?.isFSAAllowCarryOver && plans.FSA?.isFSA ? (
                                            <Col lg={3}>

                                                <div className="form-check form-switch form-toggle">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                        role="switch"
                                                        checked={plans.FSA?.isFSAAllowGracePeriod || false}
                                                        onChange={(e) => handleToggleChange(e, 'FSA', 'isFSAAllowGracePeriod')}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}

                                        {plans.DCA?.isDCA ? (
                                            <Col lg={3}>
                                                <div className="form-check form-switch form-toggle">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                        role="switch"
                                                        checked={plans.DCA?.isDCAAllowGracePeriod || false}
                                                        onChange={(e) => handleToggleChange(e, 'DCA', 'isDCAAllowGracePeriod')}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}

                                        {!plans.LPFSA?.isLPFSAAllowCarryOver && ((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                            <Col lg={3}>
                                                <div className="form-check form-switch form-toggle">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input form-check-input-toggel toggle-input-switch"
                                                        role="switch"
                                                        checked={plans.LPFSA?.isLPFSAAllowGracePeriod || false}
                                                        onChange={(e) => handleToggleChange(e, 'LPFSA', 'isLPFSAAllowGracePeriod', plans.LPFSA?.planId)}
                                                        disabled={isDisabled}
                                                    />
                                                </div>
                                            </Col>) : (<Col lg={3} />)}
                                    </Row>

                                    {((!plans.FSA?.isFSAAllowCarryOver && plans.FSA?.isFSAAllowGracePeriod) || plans.DCA?.isDCAAllowGracePeriod || (!plans.LPFSA?.isLPFSAAllowCarryOver && plans.LPFSA?.isLPFSAAllowGracePeriod)) &&

                                        (<>

                                            <Row >
                                                <Col lg={3} className='mb-3'>
                                                    <Form.Label>
                                                        Grace Period End Date

                                                    </Form.Label>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id={`tooltip-grace-period-`}>
                                                                Grace Period is defined as the period of time up to 75 days from the end of the plan year in which participants can incur expenses that can be reimbursed from the prior plan year balance                                       </Tooltip>
                                                        }
                                                    >
                                                        <span className="btn btn-sm btn-bg-soft-secondary ms-2">
                                                            <i className="dropicons dripicons-information"></i>
                                                        </span>
                                                    </OverlayTrigger>
                                                </Col>


                                                {!plans.FSA?.isFSAAllowCarryOver && plans.FSA?.isFSAAllowGracePeriod && plans.FSA?.isFSA ? (
                                                    <Col lg={3}>
                                                        <div className="d-flex ">
                                                            <span className="input-group-text">
                                                                <i className="  far fa-calendar-alt"></i>
                                                            </span>

                                                            <DatePicker
                                                                className={`form-control ${validationErrors && validationErrors.fsaGracePeriodEnd ? 'is-invalid' : ''}`}
                                                                selected={plans.FSA?.fsaGracePeriodEnd || ''}
                                                                onChange={(date) => handleDateChange('FSA', 'fsaGracePeriodEnd', date)}
                                                                placeholderText="Please select date" disabled={isDisabled}
                                                            />

                                                        </div>

                                                    </Col>) : (<Col lg={3} />)}



                                                {plans.DCA?.isDCAAllowGracePeriod && plans.DCA?.isDCA ? (
                                                    <Col lg={3}>
                                                        <div className="d-flex ">
                                                            <span className="input-group-text">
                                                                <i className="  far fa-calendar-alt"></i>
                                                            </span>

                                                            <DatePicker
                                                                className={`form-control ${validationErrors && validationErrors.dcaGracePeriodEnd ? 'is-invalid' : ''}`}
                                                                selected={plans.DCA?.dcaGracePeriodEnd || ''}
                                                                onChange={(date) => handleDateChange('DCA', 'dcaGracePeriodEnd', date)}
                                                                placeholderText="Please select date" disabled={isDisabled}
                                                            />

                                                        </div>
                                                    </Col>) : (<Col lg={3} />)}


                                                {!plans.LPFSA?.isLPFSAAllowCarryOver && plans.LPFSA?.isLPFSAAllowGracePeriod && ((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                                    <Col lg={3}>
                                                        <div className="d-flex ">
                                                            <span className="input-group-text">
                                                                <i className="  far fa-calendar-alt"></i>
                                                            </span>

                                                            <DatePicker
                                                                className={`form-control ${validationErrors && validationErrors.lpfsaGracePeriodEnd ? 'is-invalid' : ''}`}
                                                                selected={plans.LPFSA?.lpfsaGracePeriodEnd || ''}
                                                                onChange={(date) => handleDateChange('LPFSA', 'lpfsaGracePeriodEnd', date, plans.LPFSA?.planId)}
                                                                placeholderText="Please select date" disabled={isDisabled}
                                                            />

                                                        </div>
                                                    </Col>) : (<Col lg={3} />)}
                                            </Row>

                                            <Row >
                                                <Col lg={3}>
                                                    <InputGroup className="mb-3">
                                                        <Form.Label>
                                                            When will the Runout Period Begin?
                                                        </Form.Label>
                                                    </InputGroup>

                                                </Col>
                                                {!plans.FSA?.isFSAAllowCarryOver && plans.FSA?.isFSAAllowGracePeriod && plans.FSA?.isFSA ? (
                                                    <Col lg={3} className="mb-3">

                                                        <Select
                                                            name="fsaRunoutPeriodBegin"
                                                            placeholder="Please select"
                                                            options={runoutBeginOptions}
                                                            value={runoutBeginOptions.find(option => option.value === plans.FSA?.fsaRunoutPeriodBegin)}
                                                            styles={customStyles(validationErrors && validationErrors.fsaRunoutPeriodBegin)}
                                                            isDisabled={isDisabled}
                                                            onChange={(selectedOption) => handleSelectChange(selectedOption, 'FSA', 'fsaRunoutPeriodBegin')}  // Handle change
                                                        />


                                                    </Col>) : (<Col lg={3} />)}
                                                {plans.DCA?.isDCAAllowGracePeriod && plans.DCA?.isDCA ? (
                                                    <Col lg={3} className="mb-3">

                                                        <Select
                                                            name="dcaRunoutPeriodBegin"
                                                            placeholder="Please select"
                                                            options={runoutBeginOptions}
                                                            value={runoutBeginOptions.find(option => option.value === plans.DCA?.dcaRunoutPeriodBegin)}
                                                            styles={customStyles(validationErrors && validationErrors.dcaRunoutPeriodBegin)}
                                                            isDisabled={isDisabled}
                                                            onChange={(selectedOption) => handleSelectChange(selectedOption, 'DCA', 'dcaRunoutPeriodBegin')}  // Handle change
                                                        />


                                                    </Col>) : (<Col lg={3} />)}


                                                {!plans.LPFSA?.isLPFSAAllowCarryOver && plans.LPFSA?.isLPFSAAllowGracePeriod && ((plans.generalInfo?.isHSA && plans.generalInfo?.isOfferLPFSA) || plans.LPFSA?.isLPFSA) ? (
                                                    <Col lg={3} className="mb-3">

                                                        <Select
                                                            name="lpfsaRunoutPeriodBegin"
                                                            placeholder="Please select"
                                                            options={runoutBeginOptions}
                                                            value={runoutBeginOptions.find(option => option.value === plans.LPFSA?.lpfsaRunoutPeriodBegin)}
                                                            styles={customStyles(validationErrors && validationErrors.lpfsaRunoutPeriodBegin)}
                                                            isDisabled={isDisabled}
                                                            onChange={(selectedOption) => handleSelectChange(selectedOption, 'LPFSA', 'lpfsaRunoutPeriodBegin', plans.LPFSA?.planId)}  // Handle change
                                                        />



                                                    </Col>) : (<Col lg={3} />)}
                                            </Row>
                                        </>)}


                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            
        </>


    );
});

export default FSA;